import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import CustomerLocationsTab from './CustomerLocationsTab';

const CustomerProfileModal = ({ customer, onClose, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    name: customer?.name,
    email: customer?.email,
    phone: customer?.phone,
    company: customer?.company,
    location: customer?.location,
    address: customer?.address,
    status: customer?.status
  });

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Pending', label: 'Pending' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave({ ...customer, ...formData });
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: 'User' },
    { id: 'locations', label: 'Locations', icon: 'MapPin' },
    { id: 'equipment', label: 'Equipment', icon: 'Wrench' },
    { id: 'history', label: 'Service History', icon: 'Clock' }
  ];

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="User" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                {customer?.name}
              </h2>
              <p className="text-sm text-muted-foreground">{customer?.company}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </Button>
        </div>

        <div className="border-b border-border overflow-x-auto">
          <div className="flex gap-1 p-2 min-w-max">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-smooth flex-shrink-0 ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={tab?.icon} size={18} color="currentColor" />
                <span className="whitespace-nowrap">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-foreground">
                  Customer Information
                </h3>
                {!isEditing ? (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    iconPosition="left"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({
                          name: customer?.name,
                          email: customer?.email,
                          phone: customer?.phone,
                          company: customer?.company,
                          location: customer?.location,
                          address: customer?.address,
                          status: customer?.status
                        });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Save"
                      iconPosition="left"
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  disabled={!isEditing}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  disabled={!isEditing}
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  disabled={!isEditing}
                  required
                />
                <Input
                  label="Company"
                  type="text"
                  value={formData?.company}
                  onChange={(e) => handleInputChange('company', e?.target?.value)}
                  disabled={!isEditing}
                />
                <Input
                  label="Location"
                  type="text"
                  value={formData?.location}
                  onChange={(e) => handleInputChange('location', e?.target?.value)}
                  disabled={!isEditing}
                />
                <Select
                  label="Account Status"
                  options={statusOptions}
                  value={formData?.status}
                  onChange={(value) => handleInputChange('status', value)}
                  disabled={!isEditing}
                />
              </div>

              <Input
                label="Service Address"
                type="text"
                value={formData?.address}
                onChange={(e) => handleInputChange('address', e?.target?.value)}
                disabled={!isEditing}
              />

              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3">Account Statistics</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-2xl font-heading font-semibold text-foreground">
                      {customer?.totalServices}
                    </div>
                    <div className="text-sm text-muted-foreground">Total Services</div>
                  </div>
                  <div>
                    <div className="text-2xl font-heading font-semibold text-foreground">
                      ${customer?.totalRevenue?.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Total Revenue</div>
                  </div>
                  <div>
                    <div className="text-2xl font-heading font-semibold text-foreground">
                      {customer?.equipmentCount}
                    </div>
                    <div className="text-sm text-muted-foreground">Equipment</div>
                  </div>
                  <div>
                    <div className="text-2xl font-heading font-semibold text-foreground">
                      {formatDate(customer?.lastService)}
                    </div>
                    <div className="text-sm text-muted-foreground">Last Service</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'locations' && (
            <CustomerLocationsTab customerId={customer?.id} />
          )}

          {activeTab === 'equipment' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-foreground">
                  Equipment Inventory
                </h3>
                <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
                  Add Equipment
                </Button>
              </div>

              {customer?.equipment?.map((item, index) => (
                <div key={index} className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Wrench" size={18} color="var(--color-primary)" />
                        <h4 className="font-medium text-foreground">{item?.name}</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Model: </span>
                          <span className="text-foreground">{item?.model}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Serial: </span>
                          <span className="text-foreground">{item?.serial}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Install Date: </span>
                          <span className="text-foreground">{formatDate(item?.installDate)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Last Service: </span>
                          <span className="text-foreground">{formatDate(item?.lastService)}</span>
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon">
                      <Icon name="MoreVertical" size={18} color="var(--color-foreground)" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-foreground">
                  Service History
                </h3>
                <div className="flex gap-2">
                  <Select
                    options={[
                      { value: 'all', label: 'All Services' },
                      { value: 'maintenance', label: 'Maintenance' },
                      { value: 'repair', label: 'Repair' },
                      { value: 'installation', label: 'Installation' }
                    ]}
                    value="all"
                    onChange={() => {}}
                    className="w-40"
                  />
                </div>
              </div>

              <div className="space-y-3">
                {customer?.serviceHistory?.map((service, index) => (
                  <div key={index} className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div>
                            <h4 className="font-medium text-foreground">{service?.type}</h4>
                            <p className="text-sm text-muted-foreground">{service?.description}</p>
                          </div>
                          <span className="text-sm font-medium text-foreground whitespace-nowrap">
                            ${service?.cost?.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Icon name="Calendar" size={14} color="currentColor" />
                            <span>{formatDate(service?.date)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="User" size={14} color="currentColor" />
                            <span>{service?.technician}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="FileText" size={14} color="currentColor" />
                            <span>Ticket #{service?.ticketId}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            fullWidth
            iconName="MessageSquare"
            iconPosition="left"
            className="sm:flex-1"
          >
            Send Message
          </Button>
          <Button
            variant="outline"
            fullWidth
            iconName="Calendar"
            iconPosition="left"
            className="sm:flex-1"
          >
            Schedule Service
          </Button>
          <Button
            variant="default"
            fullWidth
            iconName="Plus"
            iconPosition="left"
            className="sm:flex-1"
          >
            New Request
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfileModal;